import React from 'react';

interface RouteInfo {
  id?: string;
  name: string;
  description?: string;
  path?: string;
  endpoint_url?: string;
  methods: string[];
  handler_code?: string;
  auth_required?: boolean;
  created_at?: string;
  last_used?: string | null;
  usage_count?: number;
  owner_user_id?: string;
}

interface RouteDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  routeInfo: RouteInfo;
  curlExamples?: Record<string, string>;
  isNewRoute?: boolean;
}

const RouteDetailsModal: React.FC<RouteDetailsModalProps> = ({
  isOpen,
  onClose,
  routeInfo,
  curlExamples,
  isNewRoute = false
}) => {
  if (!isOpen) return null;

  const generateCurlCommand = (route: RouteInfo, method: string = 'GET'): string => {
    const baseUrl = route.endpoint_url || `${window.location.origin}/api`;
    const authHeader = route.auth_required ? " \\\n  -H 'Authorization: Bearer YOUR_TOKEN'" : '';
    
    if (method === 'GET') {
      return `curl -X GET "${baseUrl}"${authHeader}`;
    } else if (method === 'POST') {
      // For POST, only include Content-Type header and empty data for routes with no parameters
      return `curl -X POST "${baseUrl}" \\
  -H 'Content-Type: application/json'${authHeader} \\
  --data '{}'`;
    } else if (method === 'PUT') {
      return `curl -X PUT "${baseUrl}" \\
  -H 'Content-Type: application/json'${authHeader} \\
  --data '{}'`;
    } else if (method === 'DELETE') {
      return `curl -X DELETE "${baseUrl}"${authHeader}`;
    }
    
    return `curl -X ${method} "${baseUrl}"${authHeader}`;
  };

  const copyCurlToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getCurlExamples = () => {
    if (curlExamples) {
      return curlExamples;
    }
    
    // Generate curl examples if not provided
    const examples: Record<string, string> = {};
    routeInfo.methods?.forEach(method => {
      examples[method.toLowerCase()] = generateCurlCommand(routeInfo, method);
    });
    return examples;
  };

  const title = isNewRoute ? "Route Created Successfully!" : "Route Details";
  const subtitle = isNewRoute 
    ? `Your API endpoint ${routeInfo.name} is ready to use`
    : `API endpoint ${routeInfo.name} information`;
  const icon = isNewRoute ? "🚀" : "📋";

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4" 
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/85 border border-zinc-700/50 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden backdrop-blur-sm" 
        style={{ zIndex: 999999 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50 bg-gradient-to-r from-slate-800/40 to-slate-700/30 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/80 to-blue-500/80 rounded-xl flex items-center justify-center text-2xl shadow-lg">
              {icon}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1 tracking-tight">{title}</h2>
              <p className="text-slate-300">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-all duration-200 hover:bg-slate-700/60 rounded-lg p-2 hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-180px)] overflow-y-auto custom-scrollbar">
          {/* Route Info */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-blue-400">ℹ️</span>
              Route Information
            </h3>
            <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 rounded-xl p-6 border border-slate-600/50 shadow-lg backdrop-blur-sm">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-slate-400 text-sm font-medium">Route Name</div>
                    <div className="text-white text-xl font-bold font-mono">{routeInfo.name}</div>
                  </div>
                  <div className="flex gap-2">
                    {routeInfo.methods?.map((method: string) => (
                      <span key={method} className="bg-gradient-to-r from-emerald-500/80 to-blue-500/80 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg">
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
                
                {routeInfo.description && (
                  <div className="border-t border-slate-600/30 pt-4">
                    <div className="text-slate-400 text-sm font-medium mb-2">Description</div>
                    <div className="text-slate-200 leading-relaxed">{routeInfo.description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Curl Examples */}
          {getCurlExamples() && Object.keys(getCurlExamples()).length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <span className="text-green-400">💻</span>
                cURL Examples
              </h3>
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 rounded-xl overflow-hidden border border-slate-600/50 shadow-lg backdrop-blur-sm">
                {Object.entries(getCurlExamples()).map(([method, curlCommand], index) => {
                  // Format the curl command with proper indentation
                  const formattedCurl = (curlCommand as string)
                    .replace(/(-H)/g, '\\\n  $1')
                    .replace(/(--data)/g, '\\\n  $1')
                    .replace(/curl /g, 'curl \\\n  ');
                  
                  return (
                    <div key={method}>
                      {index > 0 && <div className="border-t border-slate-600/30"></div>}
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                            <span className="text-white font-bold text-lg">{method.toUpperCase()}</span>
                          </div>
                          <button
                            onClick={() => copyCurlToClipboard(curlCommand as string)}
                            className="flex items-center gap-2 text-slate-300 hover:text-white transition-all duration-200 bg-slate-600/60 hover:bg-slate-500/70 px-4 py-2 rounded-lg font-medium shadow-lg hover:scale-105"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy
                          </button>
                        </div>
                        <div className="bg-slate-900/60 rounded-lg p-4">
                          <pre className="text-sm text-green-300 font-mono leading-relaxed whitespace-pre-wrap break-all">
                            <code>{formattedCurl}</code>
                          </pre>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Handler Code Section */}
          {routeInfo.handler_code && (
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <span className="text-purple-400">⚙️</span>
                Handler Code
              </h3>
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 rounded-xl overflow-hidden border border-slate-600/50 shadow-lg backdrop-blur-sm">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-600/30">
                  <span className="w-3 h-3 bg-purple-400 rounded-full"></span>
                  <span className="text-white font-bold text-lg">Python Lambda Handler</span>
                </div>
                <div className="p-6 bg-slate-900/60">
                  <pre className="text-sm text-slate-300 font-mono leading-relaxed whitespace-pre-wrap break-all max-h-80 overflow-y-auto">
                    <code>{routeInfo.handler_code}</code>
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700/50 p-4 bg-gradient-to-r from-slate-800/40 to-slate-700/30 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-slate-300">
              <span className="text-lg">💡</span>
              <span className="font-medium">Need cookies? Visit the Cookies page!</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  window.open('/?page=cookies', '_blank');
                }}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500/80 to-emerald-600/80 hover:from-emerald-600/90 hover:to-emerald-700/90 text-white rounded-lg transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 border border-emerald-400/30"
              >
                <span>🍪</span>
                Cookies
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-blue-500/80 to-blue-600/80 hover:from-blue-600/90 hover:to-blue-700/90 text-white rounded-lg transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 border border-blue-400/30"
              >
                {isNewRoute ? 'Got it! 🎉' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteDetailsModal;